import React, { FC, PropsWithChildren, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Undefinable } from '@symfa-inc/providence-types';
import { Col, Form, Popover, Row } from 'antd';
import { RadioChangeEvent } from 'antd/lib/radio';
import { SelectValue } from 'antd/lib/select';
import classNames from 'classnames';
import { RuleObject, Store } from 'rc-field-form/es/interface';
import { HttpService, ObjectComparatorService } from '@core/services';
import { SiteHttpService } from '@core/services/http';
import {
  useDidUpdateEffect,
  useFindCountyAndJurisdictions,
  useSaveChanged,
} from '@core/utils/hooks';
import {
  arrsToSelectableObject,
  formDiffs,
  isValidForm,
  normalize,
  replaceUndefinedWithNull,
  toggleModal,
} from '@core/utils/methods';
import {
  INPUT_POSITIVE_NUMBER_VALIDATOR,
  YES_NO_GROUP,
} from '@models/constants';
import { ModalMainTypes } from '@models/enums';
import {
  AssociatedProject,
  OptionProps,
  SiteBrowse,
  StateWithCounties,
} from '@models/interfaces';
import {
  ConfirmationPopup,
  NotificationsLoader,
  Panel,
} from '@shared/components';
import {
  Autocomplete,
  Coordinate,
  Input,
  InputNumber,
  PhoneNumber,
  PrimaryButton,
  RadioGroup,
  ShowMoreInput,
  TextArea,
} from '@shared/modules';
import { CommonActions, SiteActions } from '@store/actions';
import { SharedSelectors, SiteSelectors } from '@store/selectors';
import { SiteFormProps } from './models/interfaces';
import { DisablingModal, SiteOnSwitch } from './components';

import './styles.scss';

const { useForm } = Form;

export const SiteInformation: FC<SiteFormProps> = ({
  isEditing,
  toggleEditing,
  isNew = false,
  className,
  permissions: {
    canRemoveSite,
    turnOnOffSite,
    siteInformationFields: {
      DNE,
      FALocationNumber,
      NTPRequirement: NTPRequirementPermissions,
      USID,
      accessDetails: accessDetailsPermissions,
      alternateSiteInfo,
      city,
      county,
      equipmentType,
      gateOrDoorCode: gateOrDoorCodePermissions,
      generatorInstalled,
      sqInchesEquipmentAllowed,
      sqInchesEquipmentUsed,
      sqInchesCablesAllowed,
      sqInchesCablesUsed,
      groundElevation,
      FAAStudyNumber,
      FCCASR,
      isHighRisk,
      jurisdiction,
      landlordLegalEntityName,
      latitude,
      leaseAccessRestriction: leaseAccessRestrictionPermissions,
      leasedGroundSpace,
      longitude,
      siteNote: siteNotePermissions,
      noticeComment: noticeCommentPermissions,
      isNoticeNeeded,
      parcelID,
      parcelOwner,
      primaryTech,
      primaryTechMobile,
      providenceAccessNote: providenceAccessNotePermissions,
      riskCategory,
      riskNote: riskNotePermissions,
      legacySiteID,
      siteNumber,
      state,
      streetAddress,
      submarket,
      towerHeight,
      towerOwner,
      towerOwnerSiteID,
      towerType,
      zip,
    },
  },
  data,
  history,
}: PropsWithChildren<SiteFormProps>) => {
  const dispatch = useDispatch();

  const [form] = useForm();

  const siteService = HttpService.getHttpRequests(SiteHttpService);

  const { dropdownsOptions, states } = useSelector(
    SharedSelectors.getDropdownOptionsAndStates,
  );

  const siteFetching = useSelector(SiteSelectors.isFetching);

  const [initialForm, setInitialForm] = useState<Store>({});
  const [stateId, setStateId] = useState<Undefinable<string>>('');
  const [countyOptions, setCountyOptions] = useState<OptionProps[]>([]);
  const [jurisdictionOptions, setJurisdictionOptions] = useState<OptionProps[]>(
    [],
  );
  const [disablingModalVisible, setDisablingModalVisible] =
    useState<boolean>(false);
  const [siteOnSwitch, setSiteOnSwitch] = useState<boolean>();
  const [siteOnPopconfirm, setSiteOnPopconfirm] = useState<boolean>();
  const [isRiskCategoryRequired, setRiskCategoryRequired] =
    useState<boolean>(false);
  const [areNoticeCommentsRequired, setNoticeCommentsRequired] =
    useState<boolean>(false);
  const [objectsCompare, setObjectsCompare] = useState<boolean>();

  const resetForm = (): void => {
    setStateId(data?.state || '');

    if (isNew) {
      form.resetFields();
    } else if (data) {
      const {
        accessDetails,
        gateOrDoorCode,
        leaseAccessRestriction,
        riskNote,
        siteNote,
        noticeComment,
        providenceAccessNote,
        NTPRequirement,
        ...restData
      } = data;

      form.setFieldsValue({ ...restData, ...form.getFieldsValue() });
    } else {
      form.setFieldsValue({ ...data, ...form.getFieldsValue() });
    }
  };

  useFindCountyAndJurisdictions({
    stateId,
    setCountyOptions,
    setJurisdictionOptions,
    effectDepArray: [stateId, states],
  });

  useEffect(() => {
    if (data) {
      if (isNew) {
        history?.push(`/project/create/${data}`);
        dispatch(SiteActions.clearCreatedSite.done());

        return;
      }

      resetForm();
    }
  }, [data, isEditing]);

  useEffect(() => {
    if (data) {
      setSiteOnSwitch(data.isSiteOn);
    }
  }, [data]);

  useEffect(() => {
    if (isNew) {
      if (!data && isNew) {
        setInitialForm({ ...form.getFieldsValue(), longitude: undefined });
      }
    }
  }, [isNew]);

  useEffect(
    () => (): void => {
      dispatch(CommonActions.setHasUnsubmittedData.done(false));
    },
    [],
  );

  useDidUpdateEffect(() => {
    dispatch(CommonActions.setHasUnsubmittedData.done(!objectsCompare));
  }, [objectsCompare]);

  const formatSiteForm = (): SiteBrowse => {
    const values = form.getFieldsValue();

    return formDiffs<SiteBrowse>(data, {
      ...data,
      ...values,
      longitude:
        values.longitude !== '-' ? (values.longitude as number) : undefined,
    });
  };

  const onSubmit = async (): Promise<void> => {
    if (await isValidForm(form)) {
      setObjectsCompare(true);

      try {
        const formattedFormData = formatSiteForm();

        if (isNew) {
          await siteService.createSite(formattedFormData);
        } else {
          const formDataWithNull = replaceUndefinedWithNull(formattedFormData);

          await siteService.updateSite(data?.id, formDataWithNull);

          toggleEditing?.();
        }

        NotificationsLoader.notificationSuccess(
          `Site has been ${isNew ? 'created' : 'updated'}!`,
        );
      } catch (e) {
        setObjectsCompare(false);
        console.error(e);
      }
    }
  };

  const onCancel = (): void => {
    resetForm();

    setObjectsCompare(true);

    toggleEditing?.();
  };

  const onDelete = async (): Promise<void> => {
    try {
      await siteService.deleteSite(data?.id);

      NotificationsLoader.notificationSuccess('Site has been deleted!');
    } catch (e) {
      console.error(e);
    }

    history?.push('/site/browse');
  };

  const changeSiteState = (
    checked: boolean,
    disablingNote?: string,
  ): Promise<SiteBrowse> =>
    siteService.updateSite(data?.id, {
      isSiteOn: checked,
      disablingNote,
    } as SiteBrowse);

  const onToggleSite = (): void => {
    setDisablingModalVisible(!disablingModalVisible);
  };

  const setDisablingNotesValue = async (value: string): Promise<void> => {
    await changeSiteState(false, value);
    onToggleSite();
  };

  const onChange = (): void => {
    const {
      latitude: initialLatitude,
      longitude: initialLongitude,
      id,
      pictures,
      isSiteOn,
      associatedProjects,
      ...rest
    } = data;

    const isHighRiskFieldData = form.getFieldValue('isHighRisk');

    setRiskCategoryRequired(isHighRiskFieldData);

    setObjectsCompare(
      ObjectComparatorService.objectsCompare(
        {
          ...form.getFieldsValue(),
          longitude: Number.isNaN(form.getFieldsValue().longitude)
            ? undefined
            : form.getFieldsValue().longitude,
        },
        isNew
          ? initialForm
          : {
              ...rest,
              latitude: parseFloat(initialLatitude),
              longitude: parseFloat(initialLongitude),
            },
        true,
      ),
    );
  };

  const buttonsDisabled = siteFetching || objectsCompare !== false;

  const isSwitchDisabled =
    data.isSiteOn &&
    data?.associatedProjects.length &&
    data?.associatedProjects.some((project: AssociatedProject) =>
      ['Active', 'Authorized'].includes(project?.projectStatus?.value),
    );

  useSaveChanged(isEditing, onSubmit, onCancel);

  return (
    <Form
      labelCol={{ span: 8 }}
      wrapperCol={{ span: 14 }}
      className={classNames('prov-site-information', className)}
      form={form}
      initialValues={{ longitude: '-' }}
      onChange={onChange}
    >
      <DisablingModal
        visible={disablingModalVisible}
        modalType={ModalMainTypes.Add}
        toggleModal={toggleModal(setDisablingModalVisible)}
        fieldSetter={setDisablingNotesValue}
      />
      <div
        className={classNames('tabs-wrap', {
          'tabs-wrap_with-actions': isNew || isEditing,
        })}
      >
        <Panel
          header={
            <Row
              justify="space-between"
              className="prov-site-information__header"
            >
              <Col className="header-title">Site Information</Col>
              {!isNew && turnOnOffSite && (
                <Col
                  span={6}
                  className={classNames('prov-site-information__info', {
                    'disabled-mode': !isSwitchDisabled,
                  })}
                >
                  <Popover
                    content={
                      <ConfirmationPopup
                        onOk={(): void => {
                          changeSiteState(true);
                          setSiteOnPopconfirm(false);
                        }}
                        onCancel={(): void => setSiteOnPopconfirm(false)}
                        text="Are you sure you want to reactivate this site?"
                      />
                    }
                    placement="top"
                    visible={!disablingModalVisible && siteOnPopconfirm}
                  >
                    <SiteOnSwitch
                      onChange={(checked: boolean): void => {
                        const callback = checked
                          ? setSiteOnPopconfirm
                          : onToggleSite;

                        callback(checked);
                      }}
                      switchState={siteOnSwitch}
                      disabled={isSwitchDisabled}
                    />
                  </Popover>
                </Col>
              )}
            </Row>
          }
          className="prov-site-information__panel"
        >
          <Row>
            <Col span="8">
              <InputNumber
                id="FALocationNumber"
                label="FA Location Number"
                elementProps={{
                  maxLength: 8,
                  minLength: 8,
                  onChange,
                }}
                isEditing={isEditing && !!FALocationNumber}
                formItemProps={{
                  normalize,
                  rules: [
                    {
                      required: true,
                      message: 'FA Location Number is required!',
                    },
                    INPUT_POSITIVE_NUMBER_VALIDATOR,
                    {
                      validator: async (
                        _: RuleObject,
                        value: Undefinable<number>,
                      ): Promise<void> => {
                        if (value) {
                          if (`${value}`.length < 8) {
                            throw new Error(
                              'FA Location Number must be 8 characters!',
                            );
                          }

                          if (
                            !(await siteService.validateFALocationNumber(
                              value,
                              data?.id,
                            ))
                          ) {
                            throw new Error(
                              'FA Location Number must be unique!',
                            );
                          }
                        }
                      },
                    },
                  ],
                }}
              />
              <Input
                id="siteNumber"
                label="Site Number"
                isEditing={isEditing && !!siteNumber}
              />
              <Input
                id="legacySiteID"
                label="Legacy Site ID"
                isEditing={isEditing && !!legacySiteID}
              />
              <Input
                id="alternateSiteInfo"
                label="Alternate Site Info"
                isEditing={isEditing && !!alternateSiteInfo}
              />
              <InputNumber
                id="USID"
                label="USID"
                isEditing={isEditing && !!USID}
                formItemProps={{
                  normalize,
                  rules: [INPUT_POSITIVE_NUMBER_VALIDATOR],
                }}
                elementProps={{ onChange }}
              />
              <Autocomplete
                id="towerOwner"
                label="Tower Owner"
                isEditing={isEditing && !!towerOwner}
                options={dropdownsOptions?.TowerOwner || []}
                elementProps={{ onChange, allowClear: true }}
              />
              <Input
                id="towerOwnerSiteID"
                label="Tower Owner Site ID"
                isEditing={isEditing && !!towerOwnerSiteID}
              />
              <Input
                id="parcelID"
                label="Parcel ID"
                isEditing={isEditing && !!parcelID}
              />
              <Input
                id="parcelOwner"
                label="Parcel Owner"
                isEditing={isEditing && !!parcelOwner}
              />
              <Input
                id="landlordLegalEntityName"
                label="Landlord Legal Entity Name"
                isEditing={isEditing && !!landlordLegalEntityName}
              />
              <Input
                id="streetAddress"
                label="Street Address"
                isEditing={isEditing && !!streetAddress}
              />
              <Input id="city" label="City" isEditing={isEditing && !!city} />
              <Autocomplete
                id="state"
                label="State"
                options={arrsToSelectableObject<StateWithCounties>(
                  states,
                  'id',
                  'name',
                )}
                elementProps={{
                  allowClear: true,
                  onChange: (value: SelectValue): void => {
                    form.setFieldsValue({
                      county: null,
                      jurisdiction: null,
                    });
                    setStateId(value as string);
                    onChange();
                  },
                }}
                isEditing={isEditing && !!state}
              />
              <InputNumber
                id="zip"
                label="Zip"
                isEditing={isEditing && !!zip}
                formItemProps={{
                  normalize,
                  rules: [INPUT_POSITIVE_NUMBER_VALIDATOR],
                }}
                elementProps={{ onChange }}
              />
              <Autocomplete
                id="county"
                label="County"
                isEditing={isEditing && !!county}
                options={stateId ? countyOptions : []}
                elementProps={{ onChange, allowClear: true }}
              />
              <Autocomplete
                id="jurisdiction"
                label="Jurisdiction"
                isEditing={isEditing && !!jurisdiction}
                options={stateId ? jurisdictionOptions : []}
                elementProps={{ onChange, allowClear: true }}
              />
              <Coordinate
                id="latitude"
                label="Latitude"
                isEditing={isEditing && !!latitude}
              />
              <Coordinate
                id="longitude"
                label="Longitude"
                isEditing={isEditing && !!longitude}
              />
              <Autocomplete
                id="submarket"
                label="Submarket"
                options={dropdownsOptions?.Submarket || []}
                isEditing={isEditing && !!submarket}
                elementProps={{ onChange, allowClear: true }}
              />
            </Col>
            <Col span="8">
              <Autocomplete
                id="towerType"
                label="Tower Type"
                isEditing={isEditing && !!towerType}
                options={dropdownsOptions?.TowerType || []}
                elementProps={{ onChange, allowClear: true }}
              />
              <InputNumber
                id="towerHeight"
                label="Tower Height (ft)"
                isEditing={isEditing && !!towerHeight}
                formItemProps={{
                  normalize,
                  rules: [INPUT_POSITIVE_NUMBER_VALIDATOR],
                }}
                elementProps={{ onChange }}
              />
              <InputNumber
                id="DNE"
                label="DNE (ft)"
                isEditing={isEditing && !!DNE}
                formItemProps={{
                  normalize,
                  rules: [INPUT_POSITIVE_NUMBER_VALIDATOR],
                }}
                elementProps={{ onChange }}
              />
              <InputNumber
                id="groundElevation"
                label="Ground Elevation"
                isEditing={isEditing && !!groundElevation}
                formItemProps={{
                  normalize,
                }}
                elementProps={{ onChange }}
              />
              <Input
                id="FAAStudyNumber"
                label="FAA Study Number"
                isEditing={isEditing && !!FAAStudyNumber}
              />
              <Input
                id="FCCASR"
                label="FCC ASR"
                isEditing={isEditing && !!FCCASR}
              />
              <Input
                id="leasedGroundSpace"
                label="Leased Ground Space"
                isEditing={isEditing && !!leasedGroundSpace}
              />
              <Autocomplete
                id="equipmentType"
                label="Equipment Type"
                isEditing={isEditing && !!equipmentType}
                options={dropdownsOptions?.EquipmentType || []}
                elementProps={{ onChange, allowClear: true }}
              />
              <Autocomplete
                id="generatorInstalled"
                label="Generator Installed"
                isEditing={isEditing && !!generatorInstalled}
                options={dropdownsOptions?.GeneratorInstalled || []}
                elementProps={{ onChange, allowClear: true }}
              />
              <Input
                id="sqInchesEquipmentAllowed"
                label="Sq. Inches Equipment Allowed"
                isEditing={isEditing && !!sqInchesEquipmentAllowed}
              />
              <Input
                id="sqInchesEquipmentUsed"
                label="Sq. Inches Equipment Used"
                isEditing={isEditing && !!sqInchesEquipmentUsed}
              />
              <Input
                id="sqInchesCablesAllowed"
                label="Sq. Inches Cables Allowed"
                isEditing={isEditing && !!sqInchesCablesAllowed}
              />
              <Input
                id="sqInchesCablesUsed"
                label="Sq. Inches Cables Used"
                isEditing={isEditing && !!sqInchesCablesUsed}
              />
              <ShowMoreInput
                id="NTPRequirement"
                label="NTP Requirements"
                data={data?.NTPRequirement}
                form={form}
                isEditing={isEditing && !!NTPRequirementPermissions}
              />
              <ShowMoreInput
                id="siteNote"
                label="Site Notes"
                data={data.siteNote}
                form={form}
                isEditing={isEditing && !!siteNotePermissions}
              />
              {!isNew && !data.isSiteOn && (
                <TextArea
                  id="disablingNote"
                  label="Disabling notes"
                  isEditing={false}
                />
              )}
            </Col>
            <Col span="8">
              <RadioGroup
                id="isHighRisk"
                label="High Risk"
                buttons={YES_NO_GROUP}
                isEditing={isEditing && !!isHighRisk}
                elementProps={{
                  onChange: (event: RadioChangeEvent): void =>
                    setRiskCategoryRequired(event.target.value),
                }}
              />
              <Autocomplete
                id="riskCategory"
                label="Risk Category"
                options={dropdownsOptions?.RiskCategory || []}
                isEditing={isEditing && !!riskCategory}
                formItemProps={{
                  rules: [
                    {
                      required: isRiskCategoryRequired,
                      message: 'Risk Category is required!',
                    },
                  ],
                }}
                elementProps={{ onChange, allowClear: true }}
              />
              <ShowMoreInput
                id="riskNote"
                label="Risk Note"
                data={data.riskNote}
                form={form}
                isEditing={isEditing && !!riskNotePermissions}
                formItemProps={{
                  rules: [
                    {
                      required: isRiskCategoryRequired,
                      message: 'Risk Note is required!',
                    },
                  ],
                }}
              />
              <ShowMoreInput
                id="providenceAccessNote"
                label="Providence Access Notes"
                data={data.providenceAccessNote}
                form={form}
                isEditing={isEditing && !!providenceAccessNotePermissions}
              />
              <ShowMoreInput
                id="accessDetails"
                label="Access Details"
                data={data.accessDetails}
                form={form}
                isEditing={isEditing && !!accessDetailsPermissions}
              />
              <ShowMoreInput
                id="gateOrDoorCode"
                label="Gate/Door Codes"
                data={data.gateOrDoorCode}
                form={form}
                isEditing={isEditing && !!gateOrDoorCodePermissions}
              />
              <RadioGroup
                id="isNoticeNeeded"
                label="Notice Needed"
                buttons={YES_NO_GROUP}
                isEditing={isEditing && !!isNoticeNeeded}
                elementProps={{
                  onChange: (e: RadioChangeEvent) => {
                    setNoticeCommentsRequired(e.target.value);
                  },
                }}
              />
              <ShowMoreInput
                id="noticeComment"
                label="Notice Comments"
                data={data.noticeComment}
                form={form}
                isEditing={isEditing && !!noticeCommentPermissions}
                formItemProps={{
                  rules: [
                    {
                      required: areNoticeCommentsRequired,
                      message: 'Notice Comments are required!',
                    },
                  ],
                }}
              />
              <ShowMoreInput
                id="leaseAccessRestriction"
                label="Lease Access Restrictions"
                data={data.leaseAccessRestriction}
                form={form}
                isEditing={isEditing && !!leaseAccessRestrictionPermissions}
              />
              <Input
                id="primaryTech"
                label="Primary Tech"
                isEditing={isEditing && !!primaryTech}
              />
              <PhoneNumber
                id="primaryTechMobile"
                label="Primary Tech Mobile"
                isEditing={isEditing && !!primaryTechMobile}
              />
            </Col>
          </Row>
        </Panel>
      </div>
      {isEditing && (
        <Row className="prov-site-information__btn-wrap">
          <div>
            <PrimaryButton
              htmlType="submit"
              title="Submit"
              disabled={buttonsDisabled}
              onClick={onSubmit}
            />
            <PrimaryButton
              title="Cancel"
              disabled={buttonsDisabled}
              type="default"
              onClick={onCancel}
            />
          </div>
          {!isNew && canRemoveSite && (
            <PrimaryButton
              className="site-delete"
              title="Delete this site"
              type="danger"
              onClick={onDelete}
            />
          )}
        </Row>
      )}
    </Form>
  );
};
